import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Palette, Plus, ExternalLink, ChevronDown, ChevronUp, Image, Upload, Link2, Unlink, AlertTriangle, Search as SearchIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useIssueDesignItems, designItemKeys } from '../../hooks/useDesignItems';
import { useQueryClient } from '@tanstack/react-query';
import { removeDesignLink } from '../../services/designItemService';
import DesignLinkModal from './DesignLinkModal';
import ImpactAnalysisPanel from './ImpactAnalysisPanel';

interface Props {
  projectId: string;
  issueId: string;
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  DRAFT: { label: '초안', bg: 'bg-gray-100', text: 'text-gray-700' },
  IN_REVIEW: { label: '리뷰 중', bg: 'bg-blue-100', text: 'text-blue-700' },
  REVISION: { label: '수정 요청', bg: 'bg-amber-100', text: 'text-amber-700' },
  APPROVED: { label: '승인', bg: 'bg-green-100', text: 'text-green-700' },
  HANDED_OFF: { label: '핸드오프', bg: 'bg-purple-100', text: 'text-purple-700' },
  NEEDS_UPDATE: { label: '업데이트 필요', bg: 'bg-red-100', text: 'text-red-700' },
};

const SOURCE_ICON: Record<string, React.ReactNode> = {
  FIGMA: <Image size={12} className="text-gray-400" />,
  UPLOAD: <Upload size={12} className="text-gray-400" />,
  EXTERNAL_URL: <Link2 size={12} className="text-gray-400" />,
};

const INITIAL_SHOW = 3;

export default function IssueDesignSection({ projectId, issueId }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { key: projectKey } = useParams<{ key: string }>();
  const { data: items, isLoading } = useIssueDesignItems(projectId, issueId);

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showImpact, setShowImpact] = useState(false);

  const handleUnlink = async (designItemId: string, linkId: string, title: string) => {
    if (!confirm(`'${title}' 디자인 연결을 해제하시겠습니까?`)) return;
    try {
      await removeDesignLink(projectId, designItemId, linkId);
      queryClient.invalidateQueries({ queryKey: designItemKeys.byIssue(projectId, issueId) });
      toast.success('디자인 연결이 해제되었습니다.');
    } catch {
      toast.error('연결 해제에 실패했습니다.');
    }
  };

  const displayItems = showAll ? items : items?.slice(0, INITIAL_SHOW);
  const hasMore = (items?.length || 0) > INITIAL_SHOW;
  const linkedIds = new Set(items?.map((i) => i.designItemId) || []);

  const handleCardClick = (designItemId: string) => {
    navigate(`/projects/${projectKey}/design-items/${designItemId}`);
  };

  return (
    <div className="border-t border-gray-100 px-5 py-3">
      <div className="flex items-center justify-between mb-3">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-400 uppercase tracking-wider">
          <Palette size={12} />
          디자인
        </h4>
        <div className="flex gap-1">
          <button
            onClick={() => setShowImpact(true)}
            className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 rounded transition-colors"
            title="영향도 분석"
          >
            <SearchIcon size={12} />
          </button>
          <button
            onClick={() => setShowLinkModal(true)}
            className="inline-flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded transition-colors"
          >
            <Plus size={12} />
            연결
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">로딩 중...</p>
      ) : !items || items.length === 0 ? (
        <p className="text-sm text-gray-500">연결된 디자인이 없습니다.</p>
      ) : (
        <div className="space-y-2">
          {displayItems?.map((item) => {
            const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.DRAFT;
            return (
              <div
                key={item.linkId}
                onClick={() => handleCardClick(item.designItemId)}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                {/* Thumbnail */}
                <div className="w-[80px] h-[60px] rounded bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                  {item.thumbnailPath ? (
                    <img
                      src={`/${item.thumbnailPath}`}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    SOURCE_ICON[item.sourceType] || <Image size={16} className="text-gray-400" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{item.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${statusCfg.bg} ${statusCfg.text}`}>
                      {statusCfg.label}
                    </span>
                    {SOURCE_ICON[item.sourceType]}
                    {item.needsReReview && (
                      <span className="text-[11px] text-amber-600 flex items-center gap-0.5">
                        <AlertTriangle size={10} />재검토
                      </span>
                    )}
                  </div>
                  {item.sourceType === 'FIGMA' && item.figmaUrl && (
                    <a
                      href={item.figmaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-0.5 text-[11px] text-primary-600 hover:underline mt-1"
                    >
                      <ExternalLink size={10} />
                      Figma에서 열기
                    </a>
                  )}
                </div>

                {/* 연결 해제 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnlink(item.designItemId, item.linkId, item.title);
                  }}
                  className="p-1 text-gray-400 hover:text-red-500 rounded shrink-0 transition-colors"
                  title="연결 해제"
                >
                  <Unlink size={14} />
                </button>
              </div>
            );
          })}

          {/* Show more */}
          {hasMore && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 w-full justify-center py-1"
            >
              {showAll ? (
                <>
                  <ChevronUp size={12} />
                  접기
                </>
              ) : (
                <>
                  <ChevronDown size={12} />
                  {(items?.length || 0) - INITIAL_SHOW}개 더보기
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <DesignLinkModal
          projectId={projectId}
          issueId={issueId}
          linkedDesignItemIds={linkedIds}
          onClose={() => setShowLinkModal(false)}
        />
      )}

      {/* Impact Analysis */}
      {showImpact && (
        <ImpactAnalysisPanel
          projectId={projectId}
          issueId={issueId}
          onClose={() => setShowImpact(false)}
        />
      )}
    </div>
  );
}
